import React, { RefObject, useEffect, useImperativeHandle, useRef, useState, ReactNode } from 'react';
import { View, Text, StyleProp, ViewStyle, StyleSheet } from 'react-native';
import { MB_Pressable, MB_PressableStateCallbackType } from '@/components/general/pressable';
import { MB_VideoObject, MB_VideoPlayerPlayStatus, MB_VideoPlayerRef } from './MB_VideoPlayerUtils';
import { MB_VideoPlayer } from './MB_VideoPlayer';
import { COLORS } from './colors';
import { MB_Modal } from '@/components/general/modal';
import { MB_Image, MB_ImageObject, mbGetImage } from '@/components/general/image';
import { isMobile } from '@/utils/general/device-info';
import { mbPlatformStyle, mbApplyTransparency } from '@/utils/general/style-utils';
import { MB_BLUR_TINT, MB_Blur } from '@/components/general/blur';
import { MB_SVG_Play, MB_SVG_BrokenFile } from '@/components/general/svg-components';

interface MB_ThumbnailVideoViewerModalRef {
  /**
   * Returns true if the modal is showing.
   */
  isShowingModal: () => boolean;

  /**
   * Returns true if the player is in full screen.
   */
  isPlayerInFullScreen: () => boolean;

  /**
   * Sets the visibility of the modal.
   */
  setModalVisibility: (isVisible: boolean) => void;
}

interface MB_ThumbnailVideoViewerModalProps {
  /**
   * The style of the thumbnail container.
   */
  thumbnailContainerStyle?: StyleProp<ViewStyle>;

  /**
   * The style of the player container.
   */
  playerContainerStyle?: StyleProp<ViewStyle>;

  /**
   * The video details.
   */
  video: MB_VideoObject | undefined;

  /**
   * The size of the thumbnail.
   */
  thumbnailSizeToUse?: 'small' | 'medium' | 'large';

  /**
   * A custom thumbnail to use.
   */
  customThumbnail?: MB_ImageObject;

  /**
   * Callback to be called when the modal is shown or hidden.
   */
  onShowHideModalUpdate?: (isShowing: boolean) => void;

  /**
   * Callback to be called when the player enters/leaves full screen.
   */
  onShowHideFullScreenPlayerUpdate?: (isFullScreen: boolean) => void;

  /**
   * Callback to be called when the thumbnail is pressed.
   */
  onThumbnailPressed?: () => void;

  /**
   * Reference to the component.
   */
  MB_ThumbnailVideoViewerModalRef?: RefObject<MB_ThumbnailVideoViewerModalRef | null>;

  /**
   * Disables the video. Useful in places where we want to implement the
   * thumbnail and the listener, but actually don't show any videos.
   */
  disableModalVideo?: boolean;

  /**
   * A custom view to show when the video is processing.
   */
  customVideoProcessingView?: React.JSX.Element;

  /**
   * A custom view to show when there isn't any thumbnails.
   */
  customNoThumbnailView?: React.JSX.Element;

  /**
   * The style of the play icon.
   */
  thumbnailPlayIconStyle?: StyleProp<ViewStyle>;

  /**
   * The size of the play icon arrow.
   */
  thumbnailPlayIconArrowSize?: number;

  /**
   * If true, hides the play icon.
   */
  hidePlayIcon?: boolean;

  /**
   * The grayscale filter to apply to the thumbnail.
   */
  grayscaleFilter?: number;

  /**
   * If true, closes the modal when the backdrop is pressed.
   */
  closeOnBackdropPress?: boolean;

  /**
   * Callback to be called when the play status of the video changes.
   */
  onPlayStatusChanged?: (status: MB_VideoPlayerPlayStatus) => void;

  /**
   * The animation duration to display and hide the modal.
   */
  animationTiming?: number;

  /**
   * If true, the component is focused.
   */
  isFocused?: boolean;

  /**
   * Callback to be called when the close button is pressed.
   */
  onCloseButtonPressed?: () => void;

  /**
   * The loading component to show while the video is loading.
   */
  loadingComponent?: ReactNode;
}

/**
 * This component is a helper component for IMB_VideoPlayer that will just show the thumbnail without loading the video player.
 * Once the user presses on the thumbnail, then the video player will load and show up in full screen mode (Atuo Full Screen Does not work on safari for now)
 * Once the user exits the full screen (Or pauses on safari while not in full screen) then the video player itself will be unloaded.
 */
const MB_ThumbnailVideoViewerModal = React.memo(
  ({
    thumbnailContainerStyle,
    playerContainerStyle,
    video,
    thumbnailSizeToUse,
    customThumbnail,
    onShowHideModalUpdate,
    onShowHideFullScreenPlayerUpdate,
    onThumbnailPressed,
    MB_ThumbnailVideoViewerModalRef,
    disableModalVideo,
    customVideoProcessingView,
    customNoThumbnailView,
    thumbnailPlayIconStyle,
    thumbnailPlayIconArrowSize,
    hidePlayIcon,
    grayscaleFilter,
    closeOnBackdropPress,
    onPlayStatusChanged,
    animationTiming,
    isFocused,
    onCloseButtonPressed,
    loadingComponent,
  }: MB_ThumbnailVideoViewerModalProps) => {
    const internalVideoPlayerRef = useRef<MB_VideoPlayerRef>(null);
    const [isShowingModal, setIsShowingModal] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);

    useImperativeHandle(MB_ThumbnailVideoViewerModalRef, () => ({
      isShowingModal: () => isShowingModal,
      isPlayerInFullScreen: () => isFullScreen,
      setModalVisibility: (isVisible: boolean) => {
        setIsShowingModal(isVisible);
      },
    }));

    const onFullscreenUpdate = (fullScreenStatus: boolean) => {
      setIsFullScreen(fullScreenStatus);
      onShowHideFullScreenPlayerUpdate?.(fullScreenStatus);
    };

    useEffect(() => {
      onShowHideModalUpdate?.(isShowingModal);
    }, [isShowingModal, onShowHideModalUpdate]);

    const onThumbnailPressedInternal = React.useCallback(() => {
      onThumbnailPressed?.();
      if (!disableModalVideo) {
        setIsShowingModal(true);
      }
    }, [disableModalVideo, onThumbnailPressed]);

    if (!video) {
      return null;
    }

    if (video.isProcessing) {
      return (
        <View style={[styles.thumbnailContainerStyle, thumbnailContainerStyle]}>
          {customVideoProcessingView ?? (
            <>
              <MB_SVG_Play size={24} color={COLORS.plum} containerStyle={styles.playIconContainer} />
              <Text style={styles.processingVideoText}>We're processing this video.{'\n'}Please check back later.</Text>
            </>
          )}
        </View>
      );
    }

    const Thumbnail = () => {
      let thumbnail = mbGetImage({ image: customThumbnail ?? video.thumbnail, options: { asUri: true }, size: thumbnailSizeToUse ?? 'small' });
      const blurViewAbsolutePositionStyle: StyleProp<ViewStyle> = thumbnail ? {} : { left: 8, top: 8, borderColor: COLORS.white };

      return (
        <MB_Pressable style={styles.thumbnailContainer} onPress={onThumbnailPressedInternal}>
          {({ hovered }: MB_PressableStateCallbackType) => {
            return (
              <View style={styles.thumbnailInnerContainer}>
                {thumbnail ? (
                  <MB_Image grayscaleFilter={grayscaleFilter} source={thumbnail} style={{ width: '100%', height: '100%' }} disableInteraction activateHover={hovered} />
                ) : (
                  <View>
                    {customNoThumbnailView ? (
                      <View>{customNoThumbnailView}</View>
                    ) : (
                      <View style={{ alignItems: 'center' }}>
                        <MB_SVG_BrokenFile color={COLORS.white} size={64} />
                        <Text style={styles.noThumbnailText}>Thumbnail not available</Text>
                      </View>
                    )}
                  </View>
                )}
                {!hidePlayIcon && (
                  <MB_Blur intensity={isMobile ? 30 : 60} tint={MB_BLUR_TINT.dark} style={[styles.playIconBlurBackground, blurViewAbsolutePositionStyle, thumbnailPlayIconStyle]}>
                    <MB_SVG_Play size={thumbnailPlayIconArrowSize ?? 13} color={COLORS.plum} />
                  </MB_Blur>
                )}
              </View>
            );
          }}
        </MB_Pressable>
      );
    };

    return (
      <View style={[styles.thumbnailContainerStyle, thumbnailContainerStyle]}>
        <MB_Modal
          isVisible={isShowingModal}
          onDismiss={setIsShowingModal}
          childrenWrapperStyle={styles.modalWrapper}
          closeOnBackdropPress={closeOnBackdropPress}
          animationInTiming={animationTiming}
          animationOutTiming={animationTiming}
          onCloseButtonPressed={onCloseButtonPressed}
          simplified={!isMobile}
        >
          <MB_VideoPlayer
            sourceUrl={video.url}
            containerStyle={[styles.playerStyle, playerContainerStyle]}
            MB_Ref={internalVideoPlayerRef}
            onFullscreenUpdate={onFullscreenUpdate}
            showControls={true}
            onReady={() => {
              internalVideoPlayerRef.current?.play();
            }}
            onPlayStatusChanged={onPlayStatusChanged}
            isFocused={isFocused}
            loadingComponent={loadingComponent}
          />
        </MB_Modal>

        <Thumbnail />
      </View>
    );
  }
);

export { MB_ThumbnailVideoViewerModal };
export type { MB_ThumbnailVideoViewerModalProps, MB_ThumbnailVideoViewerModalRef };

const styles = StyleSheet.create({
  playerStyle: {
    width: '100%',
    height: '100%',
    backgroundColor: COLORS.black,
  },
  thumbnailContainerStyle: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    backgroundColor: COLORS.plum,
    overflow: 'hidden',
  },
  playIconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    paddingStart: 4,
    backgroundColor: COLORS.lightGray,
    borderRadius: 300,
  },
  playIconBlurBackground: {
    width: 33,
    height: 33,
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 300,
    borderWidth: 1,
    borderColor: mbApplyTransparency(COLORS.white, 0.15),
    paddingStart: 2,
  },
  thumbnailContainer: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbnailInnerContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  modalWrapper: {
    ...mbPlatformStyle({
      web: {
        paddingVertical: 96,
        paddingHorizontal: 96,
      },
      mobile: {
        aspectRatio: 1,
        width: '100%',
        overflow: 'hidden',
      },
    }),
  },
  noThumbnailText: {
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.white,
    textAlign: 'center',
  },
  processingVideoText: {
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.white,
    textAlign: 'center',
    marginTop: 8,
  },
});
